export type DesignStudioFamilyId = "energy" | "classic" | "nature" | "sky" | "neon" | "soft";

export interface IDesignStudioFamily {
  id: DesignStudioFamilyId;
  name: string;
}

/** Every studio colour, as the studio stores it: six hex digits, no '#'. */
export interface IDesignStudioColors {
  backgroundColorPrimary: string;
  backgroundColorSecondary: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  accentColorFrom: string;
  accentColorTo: string;
  accentTextColor: string;
}

export interface IDesignStudioPreset {
  key: string;
  name: string;
  family: DesignStudioFamilyId;
  colors: IDesignStudioColors;
}

export const designStudioFamilies: IDesignStudioFamily[] = [
  { id: "energy", name: "Енергия" },
  { id: "classic", name: "Класика" },
  { id: "nature", name: "Природа" },
  { id: "sky", name: "Небе" },
  { id: "neon", name: "Неон" },
  { id: "soft", name: "Нежни" },
];

// Curated in ticket 1077: 43 candidates tested for contrast and distinctness, cut to 20.
export const designStudioPresets: IDesignStudioPreset[] = [
  {
    key: "blaze",
    name: "Огън",
    family: "energy",
    colors: {
      backgroundColorPrimary: "f97316",
      backgroundColorSecondary: "431407",
      buttonBackgroundColor: "c2410c",
      buttonTextColor: "ffffff",
      accentColorFrom: "d9480f",
      accentColorTo: "c92a2a",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "amber-glow",
    name: "Кехлибар",
    family: "energy",
    colors: {
      backgroundColorPrimary: "f59e0b",
      backgroundColorSecondary: "3d1e05",
      buttonBackgroundColor: "f59e0b",
      buttonTextColor: "15102b",
      accentColorFrom: "fbbf24",
      accentColorTo: "f97316",
      accentTextColor: "15102b",
    },
  },
  {
    key: "electric-lime",
    name: "Електрик лайм",
    family: "energy",
    colors: {
      backgroundColorPrimary: "84cc16",
      backgroundColorSecondary: "1a2e05",
      buttonBackgroundColor: "a3e635",
      buttonTextColor: "15102b",
      accentColorFrom: "bef264",
      accentColorTo: "5eead4",
      accentTextColor: "15102b",
    },
  },
  {
    key: "cobalt-coral",
    name: "Кобалт и корал",
    family: "energy",
    colors: {
      backgroundColorPrimary: "1d4ed8",
      backgroundColorSecondary: "172554",
      buttonBackgroundColor: "1d4ed8",
      buttonTextColor: "ffffff",
      accentColorFrom: "d63a4a",
      accentColorTo: "b8400f",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "bordeaux",
    name: "Бордо",
    family: "classic",
    colors: {
      backgroundColorPrimary: "9f1239",
      backgroundColorSecondary: "2a0a14",
      buttonBackgroundColor: "a3123f",
      buttonTextColor: "ffffff",
      accentColorFrom: "d61f69",
      accentColorTo: "a3123f",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "obsidian-gold",
    name: "Обсидиан и злато",
    family: "classic",
    colors: {
      backgroundColorPrimary: "a16207",
      backgroundColorSecondary: "1c1917",
      buttonBackgroundColor: "c9a227",
      buttonTextColor: "15102b",
      accentColorFrom: "ecd896",
      accentColorTo: "b8902f",
      accentTextColor: "15102b",
    },
  },
  {
    key: "silver-screen",
    name: "Сребърен екран",
    family: "classic",
    colors: {
      backgroundColorPrimary: "e5e7eb",
      backgroundColorSecondary: "111827",
      buttonBackgroundColor: "f3f4f6",
      buttonTextColor: "15102b",
      accentColorFrom: "ffffff",
      accentColorTo: "d1d5db",
      accentTextColor: "15102b",
    },
  },
  {
    key: "cashmere",
    name: "Кашмир",
    family: "classic",
    colors: {
      backgroundColorPrimary: "a8a29e",
      backgroundColorSecondary: "1c1917",
      buttonBackgroundColor: "78716c",
      buttonTextColor: "ffffff",
      accentColorFrom: "e7e5e4",
      accentColorTo: "c4b5a5",
      accentTextColor: "15102b",
    },
  },
  {
    key: "emerald-luxe",
    name: "Смарагд",
    family: "nature",
    colors: {
      backgroundColorPrimary: "10b981",
      backgroundColorSecondary: "022c22",
      buttonBackgroundColor: "047857",
      buttonTextColor: "ffffff",
      accentColorFrom: "f5d77a",
      accentColorTo: "d4a72c",
      accentTextColor: "15102b",
    },
  },
  {
    key: "eucalyptus",
    name: "Евкалипт",
    family: "nature",
    colors: {
      backgroundColorPrimary: "9ccfb8",
      backgroundColorSecondary: "1f2d2a",
      buttonBackgroundColor: "3d6b5c",
      buttonTextColor: "ffffff",
      accentColorFrom: "b8dcc8",
      accentColorTo: "8fbfa8",
      accentTextColor: "15102b",
    },
  },
  {
    key: "lagoon",
    name: "Лагуна",
    family: "nature",
    colors: {
      backgroundColorPrimary: "22d3ee",
      backgroundColorSecondary: "083344",
      buttonBackgroundColor: "0e7490",
      buttonTextColor: "ffffff",
      accentColorFrom: "22d3ee",
      accentColorTo: "34d399",
      accentTextColor: "15102b",
    },
  },
  {
    key: "deep-ocean",
    name: "Дълбок океан",
    family: "sky",
    colors: {
      backgroundColorPrimary: "0ea5e9",
      backgroundColorSecondary: "082f49",
      buttonBackgroundColor: "0369a1",
      buttonTextColor: "ffffff",
      accentColorFrom: "0284c7",
      accentColorTo: "1d4ed8",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "azure",
    name: "Лазур",
    family: "sky",
    colors: {
      backgroundColorPrimary: "3b82f6",
      backgroundColorSecondary: "172554",
      buttonBackgroundColor: "2563eb",
      buttonTextColor: "ffffff",
      accentColorFrom: "38bdf8",
      accentColorTo: "818cf8",
      accentTextColor: "15102b",
    },
  },
  {
    key: "twilight",
    name: "Здрач",
    family: "sky",
    colors: {
      backgroundColorPrimary: "818cf8",
      backgroundColorSecondary: "1e1b4b",
      buttonBackgroundColor: "4f46e5",
      buttonTextColor: "ffffff",
      accentColorFrom: "6366f1",
      accentColorTo: "c026d3",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "synthwave",
    name: "Синтуейв",
    family: "neon",
    colors: {
      backgroundColorPrimary: "f0abfc",
      backgroundColorSecondary: "2e1065",
      buttonBackgroundColor: "c026d3",
      buttonTextColor: "ffffff",
      accentColorFrom: "db2777",
      accentColorTo: "7e22ce",
      accentTextColor: "ffffff",
    },
  },
  {
    key: "tokyo-night",
    name: "Токийска нощ",
    family: "neon",
    colors: {
      backgroundColorPrimary: "7aa2f7",
      backgroundColorSecondary: "1a1b26",
      buttonBackgroundColor: "7aa2f7",
      buttonTextColor: "15102b",
      accentColorFrom: "bb9af7",
      accentColorTo: "f7768e",
      accentTextColor: "15102b",
    },
  },
  {
    key: "miami",
    name: "Маями",
    family: "neon",
    colors: {
      backgroundColorPrimary: "f472b6",
      backgroundColorSecondary: "0c4a6e",
      buttonBackgroundColor: "22d3ee",
      buttonTextColor: "15102b",
      accentColorFrom: "f9a8d4",
      accentColorTo: "67e8f9",
      accentTextColor: "15102b",
    },
  },
  {
    key: "rose-gold",
    name: "Розово злато",
    family: "soft",
    colors: {
      backgroundColorPrimary: "f0a6a0",
      backgroundColorSecondary: "3a1c26",
      buttonBackgroundColor: "a34d68",
      buttonTextColor: "ffffff",
      accentColorFrom: "f9cfd3",
      accentColorTo: "e8939f",
      accentTextColor: "15102b",
    },
  },
  {
    key: "pearl-blush",
    name: "Перла и руж",
    family: "soft",
    colors: {
      backgroundColorPrimary: "fbcfe8",
      backgroundColorSecondary: "3b2a35",
      buttonBackgroundColor: "f6b4d4",
      buttonTextColor: "15102b",
      accentColorFrom: "fce7f3",
      accentColorTo: "f5a8c8",
      accentTextColor: "15102b",
    },
  },
  {
    key: "lavender-haze",
    name: "Лавандула",
    family: "soft",
    colors: {
      backgroundColorPrimary: "a78bfa",
      backgroundColorSecondary: "2e1065",
      buttonBackgroundColor: "6d5bd0",
      buttonTextColor: "ffffff",
      accentColorFrom: "c4b5fd",
      accentColorTo: "f5c2f7",
      accentTextColor: "15102b",
    },
  },
];
